import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Divider, Chip, Stack, Alert, AlertTitle } from '@mui/material';
import { t } from 'i18next';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import CustomDatePicker from '../../../../components/CustomDatePicker/CustomDatePicker';
import { addManageAction } from '../../../../actions/mangeActionV2';
import { showSuccessToast } from '../../../../slices/toastSlice';
import { resetGetManage } from '../../../../slices/manageSliceV2';
import { getMangeDataAction } from '../../../../actions/mangeActionV2';
import Progress from '../../../../components/Loader/Progress ';
import EventAvailable from '@mui/icons-material/EventAvailable';
import EventBusy from '@mui/icons-material/EventBusy';

/**
 * CreateJobDialog
 * Handles creating a job for a shipment.
 * Props:
 *  - open: boolean
 *  - row: shipment object (or null)
 *  - onClose: function
 *  - searchParams: URLSearchParams (used to refresh list with same query)
 */
const CreateJobDialog = ({ open, row, onClose, searchParams }) => {
  const dispatch = useDispatch();
  const { addManage } = useSelector((state) => state.manageV2) || {};

  const initialValues = React.useMemo(() => ({
    startDate: null
  }), []);

  // Latest Return Date constraint (must be at least 2 days after start date)
  const booking = row?.booking || {};
  const lrd = booking?.latestReturnDate ? dayjs(booking.latestReturnDate) : null;

  const validationSchema = React.useMemo(
    () =>
      Yup.object({
        startDate: Yup.string()
          .required(t('Start Date is required'))
          .test(
            'before-lrd-2days',
            t('Start Date must be at least 2 days before Latest Return Date'),
            (value) => {
              if (!value || !lrd) return true; // allow if no LRD available
              const v = dayjs(value);
              if (!v.isValid()) return false;
              // v must be <= (LRD - 2 days)
              const diffDays = v.startOf('day').diff(lrd.startOf('day'), 'day');
              return diffDays <= -2;
            }
          ),
      }),
  [lrd]
  );

  const handleSubmit = async (values) => {
    if (!row) return;

          
    dispatch(addManageAction({ mode: 'job', parentId: row.id, data: { startDate: values.startDate } }));



    
  };


    useEffect(() => {
      return async () => {
        await dispatch(resetGetManage());
       dispatch(getMangeDataAction({ mode: 'shipment', query: searchParams.toString() }));
      }
  }, [dispatch, searchParams]);



  useEffect(() => {


        if (addManage.loading === "succeeded") {
          dispatch(showSuccessToast(`The job has been saved successfully`));
          onClose && onClose();
        }

    

       
      
    }, [addManage,  dispatch, onClose, searchParams]);



  const renderShipmentSummary = () => {
    if (!row) return null;

  const booking = row.booking || {};
    const containers = row.containers || [];
    const totalVehicles = containers.reduce((s, c) => s + (c.shipmentItems?.length || 0), 0);
    const totalWeight = containers.reduce((s, c) => s + (c.shipmentItems?.reduce((sv, v) => sv + (v?.vehicle?.weight || 0), 0) || 0), 0);
    const erdLocal = booking?.earliestReceivingDate ? dayjs(booking.earliestReceivingDate) : null;
    const lrdLocal = booking?.latestReturnDate ? dayjs(booking.latestReturnDate) : null;
    const today = dayjs().startOf('day');
    const daysLeftLocal = lrdLocal ? lrdLocal.startOf('day').diff(today, 'day') : null;
    const lastAllowedStartLocal = lrdLocal ? lrdLocal.subtract(2, 'day') : null;
    const fmt = (d) => (d && d.isValid() ? d.format('YYYY-MM-DD') : '-');

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight:700, mb:1 }}>{t('Shipment Summary')}</Typography>
        <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'repeat(auto-fill,minmax(170px,1fr))', md:'repeat(3,1fr)'}, gap:1.5, mb:2 }}>
          {[ 
            { label: t('Shipment ID'), value: String(row?.id || '-') },
            { label: t('Entry Date'), value: String(row?.entryDate || '-') },
            { label: t('Booking Number'), value: String(booking?.number || '-') },
            { label: t('Status'), value: String(booking?.bookingStatus?.name || '-') },
            { label: t('Destination'), value: String(booking?.destination?.name || '-') },
            { label: t('Vessel'), value: String(booking?.vesselName || '-') },
            { label: t('Voyage'), value: String(booking?.voyageNumber || '-') },
            { label: t('Pickup Terminal'), value: String(booking?.pickupTerminal?.name || '-') },
            { label: t('Cutoff Date'), value: String(booking?.cutoffDate || '-') },
            { label: t('Earliest Receiving Date'), value: String(booking?.earliestReceivingDate || '-') },
            { label: t('Latest Return Date'), value: String(booking?.latestReturnDate || '-') },
          ].map((item, idx) => (
            <Box key={idx} sx={{ p:1, border:'1px solid #e2e8f0', borderRadius:2, background:'#fafbfc' }}>
              <Typography variant="caption" color="text.secondary">{item?.label}</Typography>
              <Typography variant="body2" fontWeight={600} noWrap title={item?.value}>{item?.value}</Typography>
            </Box>
          ))}
        </Box>
        {/* Key Dates Highlight */}
        <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', md:'1fr 1fr'}, gap:1.5, mb:2 }}>
          <Alert icon={<EventAvailable fontSize="small" />} severity="info" variant="outlined" sx={{ alignItems:'flex-start' }}>
            <Typography variant="caption" color="text.secondary">{t('Earliest Receiving Date')}</Typography>
            <Typography variant="body2" fontWeight={700}>{fmt(erdLocal)}</Typography>
          </Alert>
          <Alert icon={<EventBusy fontSize="small" />} severity="warning" variant="outlined" sx={{ alignItems:'flex-start' }}>
            <Typography variant="caption" color="text.secondary">{t('Latest Return Date')}</Typography>
            <Typography variant="body2" fontWeight={700}>{fmt(lrdLocal)}</Typography>
            {typeof daysLeftLocal === 'number' && (
              <Typography variant="caption" display="block">{t('Days left')}: {daysLeftLocal}</Typography>
            )}
            {lastAllowedStartLocal && (
              <Typography variant="caption" display="block" color="error.main">{t('Last allowed start date')}: {fmt(lastAllowedStartLocal)}</Typography>
            )}
          </Alert>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb:2 }}>
          <Chip size="small" color="primary" label={`${t('Containers')}: ${containers.length}`} />
            <Chip size="small" color="success" label={`${t('Vehicles')}: ${totalVehicles}`} />
            <Chip size="small" variant="outlined" label={`${t('Total Weight')}: ${totalWeight}`} />
        </Stack>
      </Box>
    );
  };

  const renderParties = () => {
    if (!row) return null;
    const parties = [
      { key: 'client', data: row.client, title: t('Client') },
      { key: 'exporter', data: row.exporter || row?.client?.exporter, title: t('Exporter') },
      { key: 'consignee', data: row.consignee, title: t('Consignee') },
    ].filter(p => p.data);
    if (!parties.length) return null;
    return (
      <Box sx={{ mb:2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight:700, mb:1 }}>{t('Parties')}</Typography>
        <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'repeat(auto-fill,minmax(220px,1fr))', md:'repeat(3,1fr)'}, gap:1.5 }}>
          {parties.map(p => (
            <Box key={p.key} sx={{ p:1.5, border:'1px solid #e2e8f0', borderRadius:2, background:'#fff' }}>
              <Typography variant="caption" color="text.secondary">{p.title}</Typography>
              <Typography variant="body2" fontWeight={600}>{p.data?.name}</Typography>
              {p.data?.phoneNumber && <Typography variant="caption" display="block">{t('Phone')}: {p.data.phoneNumber}</Typography>}
              {p.data?.email && <Typography variant="caption" display="block">{t('Email')}: {p.data.email}</Typography>}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderContainers = () => {
    if (!row) return null;
    const containers = row.containers || [];
    if (!containers.length) return null;
    return (
      <Box sx={{ mb:2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight:700, mb:1 }}>{t('Containers')}</Typography>
        <Box sx={{ maxHeight:180, overflow:'auto', border:'1px solid #e2e8f0', borderRadius:2 }}>
          <Box sx={{ display:'grid', gridTemplateColumns:'repeat(5, minmax(120px,1fr))', gap:.5, p:1, fontSize:12, fontWeight:600, background:'#f1f5f9' }}>
            <span>{t('Number')}</span>
            <span>{t('Seal')}</span>
            <span>{t('Size')}</span>
            <span>{t('Vehicles')}</span>
            <span>{t('Net Weight')}</span>
          </Box>
          {containers.map((c,i)=>(
            <Box key={i} sx={{ display:'grid', gridTemplateColumns:'repeat(5, minmax(120px,1fr))', gap:.5, p:1, fontSize:12, borderTop:'1px solid #f0f3f6', background: i%2? '#fff':'#fbfdff' }}>
              <span>{c.containerNumber || '-'}</span>
              <span>{c.sealNumber || '-'}</span>
              <span>{c.containerSize || '-'}</span>
              <span>{c.shipmentItems?.length || 0}</span>
              <span>{c.netWeight ?? 0}</span>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const handleClose = () => {
    onClose && onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight:700 }}>{t('Create Job')}</DialogTitle>
      <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ isSubmitting, errors, touched }) => (
          <Form>

             {addManage.loading === "pending" ? (
                <Box sx={{ width: "100%" }}>
                  <Progress Msg='Adding...' />
                </Box>
              ):(
            <DialogContent dividers sx={{ pb: 1 }}>



  {addManage.hasError && (
                <Box className="errorBox" sx={{ width: "100%" }}>
                  <Alert severity="error">
                    <AlertTitle>Error</AlertTitle>
                    {addManage.errorMessage ? <div>{addManage.errorMessage}</div> : ""}
                  </Alert>
                </Box>
              )}
              <Divider sx={{ my:2 }} />

              {renderShipmentSummary()}
              <Divider sx={{ my:2 }} />
              {renderParties()}
              <Divider sx={{ my:2 }} />
              {renderContainers()}
              <Divider sx={{ my:2 }} />
              <Box sx={{ maxWidth:480 }}>
                <Typography variant="subtitle2" sx={{ fontWeight:600, mb:.5 }}>{t('Start Date')}</Typography>
                
              <CustomDatePicker name="startDate" label={t('Start Date')} />
                {touched.startDate && errors.startDate && (
                  <Typography variant="caption" color="error" display="block" mt={.5}>{errors.startDate}</Typography>
                )}
                {/* Key Dates Near Picker */}
                {(() => {
                  const erdLocal = booking?.earliestReceivingDate ? dayjs(booking.earliestReceivingDate) : null;
                  const lrdLocal = booking?.latestReturnDate ? dayjs(booking.latestReturnDate) : null;
                  const today = dayjs().startOf('day');
                  const daysLeftLocal = lrdLocal ? lrdLocal.startOf('day').diff(today, 'day') : null;
                  const lastAllowedStartLocal = lrdLocal ? lrdLocal.subtract(2, 'day') : null;
                  const fmt = (d) => (d && d.isValid() ? d.format('YYYY-MM-DD') : '-');
                  return (
                    <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', sm:'1fr 1fr'}, gap:1, mt:1 }}>
                      <Box sx={{ p:1, border:'1px solid #cfe3ff', borderRadius:1.5, bgcolor:'#f6faff', display:'flex', gap:.75, alignItems:'flex-start' }}>
                        <EventAvailable fontSize="small" color="primary" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">{t('Earliest Receiving Date')}</Typography>
                          <Typography variant="body2" fontWeight={700}>{fmt(erdLocal)}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ p:1, border:'1px solid #ffe0cc', borderRadius:1.5, bgcolor:'#fff7f2', display:'flex', gap:.75, alignItems:'flex-start' }}>
                        <EventBusy fontSize="small" color="warning" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">{t('Latest Return Date')}</Typography>
                          <Typography variant="body2" fontWeight={700}>{fmt(lrdLocal)}</Typography>
                          {lastAllowedStartLocal && (
                            <Typography variant="caption" color="error.main" display="block">{t('Last allowed start date')}: {fmt(lastAllowedStartLocal)}</Typography>
                          )}
                          {typeof daysLeftLocal === 'number' && (
                            <Typography variant="caption" color="text.secondary" display="block">{t('Days left')}: {daysLeftLocal}</Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })()}
                <Typography variant="caption" color="text.secondary" display="block" mt={.5}>
                  {t('Note: Start Date must be at least 2 days before Latest Return Date')}
                </Typography>
              </Box>
            </DialogContent>

            )}




            <DialogActions>


             

             

              <Button onClick={handleClose} disabled={isSubmitting}>{t('Cancel')}</Button>
              <Button type="submit" variant="contained" disabled={isSubmitting}>{t('Confirm')}</Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

CreateJobDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  row: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
};

export default CreateJobDialog;
